
import React, { useEffect, useRef, useState } from "react";
import EditHeader from "../EditHeader/EditHeader";
import SectionHeader from "../SectionHeader/SectionHeader";
import styles from "./EditGender.module.css";
import SelectGender from "./Step2/Step2";
import AnimatedButton from "../AnimatedButton/AnimatedButton";
import { useLocation, useNavigate } from "react-router-dom";
import PopUp from "../Popup/Popup";
import { useDashboardStore } from "../../Store/agentZustandStore";
import { getUserAgentMergedDataForAgentUpdate, updateAgentFromBackend } from "../../Store/apiStore";
import api, { API_BASE_URL } from "../../Store/apiStore";
import { isPythonBackend } from "../../utils/apiBackend";
import Loader2 from "../Loader2/Loader2";

function EditGender() {
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState(null);
  const [popupMessage, setPopupMessage] = useState("");
  const [Loading, setLoading] = useState(true); // 🆕 Start with true
  const [isDataLoaded, setIsDataLoaded] = useState(false); // 🆕 Track data load

  const navigate = useNavigate();
  const location = useLocation();
  const { agentId: stateAgentId, businessId: stateBusinessId } = location.state || {};

  // Header ka naam. sessionStorage me tabhi hota hai jab user Edit Agent flow
  // se aaya ho — direct / refresh pe khaali tha, isliye neeche API se bhi bharte hain.
  const [agentnm, setAgentnm] = useState(sessionStorage.getItem("agentName") || "");
  const { setHasFetched } = useDashboardStore();

  // Baseline for comparison
  const originalGender = useRef(null);
  const originalVoice = useRef(null);

  // Current values
  const [currentGender, setCurrentGender] = useState("");
  const [currentVoice, setCurrentVoice] = useState("");

  const [stepValidation, setStepValidation] = useState({
    genderChanged: false,
    voiceSelected: false,
    isDirty: false,
  });

  const handleValidationChange = (validation) => {
    // console.log("📝 Validation changed:", validation);
    setStepValidation((prev) => ({
      ...prev,
      ...validation,
    }));
  };

  // 🆕 Fetch agent data
  useEffect(() => {
    const getAgentIds = () => ({
      finalAgentId:
        stateAgentId ||
        sessionStorage.getItem("SelectAgentId") ||
        sessionStorage.getItem("agent_id"),
      finalBusinessId:
        stateBusinessId ||
        sessionStorage.getItem("SelectAgentBusinessId") ||
        sessionStorage.getItem("businessId") ||
        sessionStorage.getItem("bId"),
    });

    const saveAgentName = (agent) => {
      if (!agent?.agentName) return;
      sessionStorage.setItem("agentName", agent.agentName);
      setAgentnm(agent.agentName);
    };

    const fetchAgentData = async () => {
      const ssGender = sessionStorage.getItem("agentGender");
      const ssPrevGender = sessionStorage.getItem("prevAgentGender");
      const ssVoice = sessionStorage.getItem("agentVoice");
      // Step2 asks Fish for voices in this language. Nothing here ever wrote it,
      // so on a Hindi agent the picker fell back to the site default and listed
      // English voices. Treat it as required: without it, fetch the agent below
      // rather than showing a list in the wrong language.
      const ssLang = sessionStorage.getItem("agentLanguageCode");

      // console.log("📦 Session storage check:", { ssGender, ssPrevGender, ssVoice });

      // If already have data
      if ((ssGender || ssPrevGender) && ssVoice && ssLang) {
        // console.log("✅ Using session storage data");
        
        const gender = ssPrevGender || ssGender || "";
        const voice = ssVoice || "";

        originalGender.current = gender;
        originalVoice.current = voice;
        
        setCurrentGender(gender);
        setCurrentVoice(voice);
        setIsDataLoaded(true);
        setLoading(false);

        // Gender/voice session se mil gaye, par header ka naam nahi — sirf naam
        // ke liye background me fetch; page ka load isse nahi rukta.
        if (!sessionStorage.getItem("agentName")) {
          const { finalAgentId, finalBusinessId } = getAgentIds();
          if (finalAgentId && finalBusinessId) {
            getUserAgentMergedDataForAgentUpdate(finalAgentId, finalBusinessId)
              .then((res) => saveAgentName(res?.data?.agent))
              .catch((err) => console.error("EditGender agent name fetch error:", err));
          }
        }
        return;
      }

      // Get agent IDs
      const { finalAgentId, finalBusinessId } = getAgentIds();

      // console.log("🔑 Agent IDs:", { finalAgentId, finalBusinessId });

      if (!finalAgentId || !finalBusinessId) {
        console.warn("⚠️ No agent IDs found - redirecting");
        navigate("/edit-agent-new");
        return;
      }

      // Fetch from API
      try {
        setLoading(true);
        // console.log("🌐 Fetching agent data from API...");
        
        const res = await getUserAgentMergedDataForAgentUpdate(finalAgentId, finalBusinessId);
        const agent = res?.data?.agent;

        if (!agent) {
          throw new Error("No agent data received");
        }

        saveAgentName(agent);

        const gender = agent?.agentGender || "female";
        const voice = agent?.agentVoice || "";

        // console.log("✅ Fetched gender data:", { gender, voice });

        // Save to session
        sessionStorage.setItem("agentGender", gender);
        sessionStorage.setItem("prevAgentGender", gender);
        sessionStorage.setItem("agentVoice", voice);
        // The agent's own language, so the voice list matches it. A Hindi agent
        // must not be offered English voices.
        if (agent?.agentLanguageCode) {
          sessionStorage.setItem("agentLanguageCode", agent.agentLanguageCode);
        }

        // Set baselines
        originalGender.current = gender;
        originalVoice.current = voice;

        // Update state
        setCurrentGender(gender);
        setCurrentVoice(voice);
        
        setStepValidation((p) => ({ ...p, isDirty: false }));
        setIsDataLoaded(true);
        
        // console.log("💾 Data loaded and saved");

      } catch (err) {
        console.error("❌ EditGender fetch error:", err);
        setPopupType("error");
        setPopupMessage("Failed to load agent data. Please try again.");
        setShowPopup(true);
      } finally {
        setLoading(false);
      }
    };

    fetchAgentData();
  }, [stateAgentId, stateBusinessId, navigate]);

  // 🆕 Handle Save with API call
  const handleClick = async () => {
    // Gender badla ya nahi, ye Step2 batata hai — sessionStorage se compare
    // karna bharosemand nahi tha. Wahan `agentGender` ek aise effect me likha
    // jaata hai jo voice list par depend karta hai; list khaali ya dhimi hui to
    // value purani hi rehti, genderChanged false nikalta, aur neeche wala guard
    // poora skip ho jaata — agent nayi gender aur PURANI gender ki voice ke
    // saath save ho jaata tha. Ab ye TTS provider ki speed se azad hai.
    const selected = (stepValidation.gender || currentGender || "").toLowerCase();
    const original = (originalGender.current || "").toLowerCase();
    const genderChanged = Boolean(selected) && selected !== original;

    if (genderChanged) {
      if (!stepValidation.voiceSelected) {
        setPopupType("failed");
        setPopupMessage("Please select a voice after changing gender!");
        setShowPopup(true);
        return;
      }

      // Show avatar update confirmation
      setPopupType("confirm");
      setPopupMessage("Agent Gender Changed. Would you like to Update Agent Avatar?");
      setShowPopup(true);
      return;
    }

    // Gender not changed -> save directly
    await saveChanges();
  };

  // 🆕 Save changes via API
  const saveChanges = async () => {
    if (!stepValidation.isDirty) {
      // console.log("⚠️ No changes to save");
      return;
    }

    try {
      setLoading(true);

      const finalAgentId =
        stateAgentId ||
        sessionStorage.getItem("SelectAgentId") ||
        sessionStorage.getItem("agent_id");

      // Gender wahi jo Step2 ne report kiya. sessionStorage me ye value ek aise
      // effect se aati hai jo voice list ke aane par hi chalta hai — list na
      // aaye to yahan PURANI gender chali jaati thi, aur user ko lagta ki save
      // ho gaya. Voice sessionStorage se hi theek hai: wo tabhi likhi jaati hai
      // jab user card pe click karta hai.
      const payload = {
        agent_id: finalAgentId,
        agentGender:
          stepValidation.gender ||
          currentGender ||
          sessionStorage.getItem("agentGender"),
        agentVoice: sessionStorage.getItem("agentVoice"),
      };

      // console.log("💾 Saving payload:", payload);

      // Python agent Retell me exist nahi karta, is liye Node ka
      // /agentV2/updateAgent client.agent.retrieve(agent_id) pe fail ho jaata
      // hai aur gender/voice kabhi save nahi hote (DB write us call ke neeche
      // hai). updateAgentBusinessPython dono columns likhta hai bina Retell ko
      // chhue — agentGender aur agentVoice wo pehle se accept karta hai.
      // Prompt regen (/edit) yahan jaan-bujh ke nahi — wo alag kaam hai.
      let res;
      if (isPythonBackend()) {
        const nodeRes = await api.patch(
          `${API_BASE_URL}/agentV2/updateAgentBusinessPython`,
          payload
        );
        res = nodeRes?.data;
      } else {
        res = await updateAgentFromBackend(payload);
      }

      // console.log("✅ Update response:", res);

      if (res?.success) {
        setPopupType("success");
        setPopupMessage("Gender and voice updated successfully!");
        setShowPopup(true);

        // Update baselines after successful save
        const newGender = sessionStorage.getItem("agentGender");
        const newVoice = sessionStorage.getItem("agentVoice");
        
        originalGender.current = newGender;
        originalVoice.current = newVoice;
        
        sessionStorage.setItem("prevAgentGender", newGender);
        
        setStepValidation((p) => ({ ...p, isDirty: false }));
        
        // Navigate to next page
        sessionStorage.setItem("nextEditRoute", "/edit-name-avtar");
      } else {
        throw new Error(res?.message || "Update failed");
      }
    } catch (error) {
      console.error("❌ Save error:", error);
      setPopupType("error");
      setPopupMessage("Failed to update gender. Please try again.");
      setShowPopup(true);
    } finally {
      setLoading(false);
    }
  };

  // 🆕 Handle popup confirm (avatar update)
  const handleConfirmAvatarUpdate = () => {
    sessionStorage.removeItem("avatar");
    setShowPopup(false);
    
    setTimeout(() => {
      navigate("/edit-name-avtar", { 
        state: { 
          isChanged: true,
          agentId: stateAgentId,
          businessId: stateBusinessId,
          isGender:sessionStorage.getItem("agentGender")
        } 
      });
    }, 500);
  };

  // 🆕 Loading state
  if (Loading || !isDataLoaded) {
    return (
      <div>
        <EditHeader title="Edit Agent" agentName={agentnm} />
        <div style={{ textAlign: "center", padding: "50px" }}>
          {/* <p>Loading gender settings...</p>
           */}
              <Loader2/>
        </div>
      </div>
    );
  }

  return (
    <div>
      <EditHeader title="Edit Agent" agentName={agentnm} />

      <div className={styles.Maindiv}>
        <SectionHeader
          heading="Select Gender"
          subheading="Select the gender of your AI receptionist for your Business"
          highlight=""
        />

        <div className={styles.container}>
          {/* 🆕 Only render after data loaded */}
          {isDataLoaded && (
            <SelectGender
              key={`${currentGender}-${currentVoice}`} // 🆕 Force re-render
              onValidationChange={handleValidationChange}
              originalGender={originalGender.current}
              originalVoice={originalVoice.current}
            />
          )}
        </div>

        {/* Save button ke peeche white bar — neeche ke voice cards button ke
            through na dikhein. */}
        <div className={styles.saveBar} />

        <div
          className={styles.stickyWrapper}
          style={{
            position: "sticky",
            bottom: 0,
            backgroundColor: "white",
            padding: "8px",
            opacity: stepValidation.isDirty ? 1 : 0.5,
            cursor: stepValidation.isDirty ? "pointer" : "not-allowed",
          }}
        >
          <AnimatedButton
            label="Save"
            isLoading={Loading}
            disabled={!stepValidation.isDirty}
            onClick={stepValidation.isDirty ? handleClick : undefined}
          />
        </div>
      </div>

      {showPopup && (
        <PopUp
          type={popupType}
          onClose={() => setShowPopup(false)}
          message={popupMessage}
          onConfirm={() => {
            if (popupType === "confirm") {
              // Avatar update confirmation
              handleConfirmAvatarUpdate();
            } else if (popupType === "success") {
              // Success - navigate to next page
              setShowPopup(false);
              const nextRoute = sessionStorage.getItem("nextEditRoute") || "/edit-name-avtar";
              navigate(nextRoute);
            }
          }}
        />
      )}
    </div>
  );
}

export default EditGender;